import React, { useRef, useEffect, useState } from "react";
import * as atlas from "azure-maps-control";
import "azure-maps-control/dist/atlas.min.css";

const azureMapsKey = process.env.REACT_APP_AZURE_MAPS_API_KEY;

const Maps = ({ provider }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const dataSourceRef = useRef(null);

  // Our single source of truth for coords (either from provider or geocode)
  const [coords, setCoords] = useState({
    latitude: provider.latitude,
    longitude: provider.longitude,
  });

  // 1️⃣ Geocode when address/postal change
  useEffect(() => {
    const { address, postal } = provider;
    if (!address && !postal) return;

    let cancelled = false;
    fetch(
      `https://atlas.microsoft.com/search/address/json?api-version=1.0&query=${encodeURIComponent(
        `${address}, ${postal}`
      )}&subscription-key=${azureMapsKey}`
    )
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        if (data.results?.length) {
          const { lat, lon } = data.results[0].position;
          setCoords({ latitude: lat, longitude: lon });
        } else {
          console.warn("No geocoding results found.");
        }
      })
      .catch((err) => console.error("Geocode error:", err));

    return () => {
      cancelled = true;
    };
  }, [provider.address, provider.postal]);

  // 2️⃣ Initialize map once
  useEffect(() => {
    if (!mapRef.current) return;

    const map = new atlas.Map(mapRef.current, {
      authOptions: {
        authType: "subscriptionKey",
        subscriptionKey: azureMapsKey,
      },
      center: [coords.longitude, coords.latitude],
      zoom: 12,
      language: "en-US",
      interactive: false,
      showFeedbackLink: false,
      disableTelemetry: true,
    });

    mapInstanceRef.current = map;

    map.events.add("ready", () => {
      // Create a data source & layer for our pin
      const ds = new atlas.source.DataSource();
      dataSourceRef.current = ds;
      map.sources.add(ds);
      map.layers.add(
        new atlas.layer.SymbolLayer(ds, null, {
          iconOptions: {
            image: "pin-round-blue",
            anchor: "center",
          },
        })
      );
      // Initial marker
      ds.add(
        new atlas.data.Feature(
          new atlas.data.Point([coords.longitude, coords.latitude])
        )
      );
    });

    return () => {
      map.dispose();
      mapInstanceRef.current = null;
    };
  }, []); // empty → only once

  // 3️⃣ Whenever coords change, re‑center & update pin
  useEffect(() => {
    const map = mapInstanceRef.current;
    const ds = dataSourceRef.current;
    if (!map || !ds) return;

    map.setCamera({ center: [coords.longitude, coords.latitude], zoom: 12 });

    ds.clear(); // remove old pin
    ds.add(
      new atlas.data.Feature(
        new atlas.data.Point([coords.longitude, coords.latitude])
      )
    );
  }, [coords]);

  return (
    <div className="md:w-1/2">
      <div
        ref={mapRef}
        style={{ width: "100%", height: "400px" }}
        className="w-full h-96 rounded-md overflow-hidden"
      />
      {coords.latitude && coords.longitude && (
        <a
          href={`https://www.google.com/maps?q=${coords.latitude},${coords.longitude}`}
          target="_blank"
          rel="noopener noreferrer"
          className="bg-[#7A00FD] hover:bg-purple-700 text-white px-6 py-2 sm:px-8 sm:py-3 mt-4 inline-block rounded-lg transition-all duration-300 transform hover:scale-105"
        >
          Open in Maps
        </a>
      )}
    </div>
  );
};

export default Maps;
